import React, { useState, useCallback } from 'react';
import Button from '@/components/UI/Button';
import FormNumberInput from '@/components/UI/FormNumberInput';
import Modal from '@/components/UI/Modal';
import { trpc } from '@/api/trpc.ts';
import { useQueryClient } from '@tanstack/react-query';
import { useTrpcMutation } from '@/api/useTrpcMutation';

interface ResetPortfolioModalProps {
    isOpen: boolean;
    onClose: () => void;
    portfolioId: string | undefined;
}

const ResetPortfolioModal: React.FC<ResetPortfolioModalProps> = ({ isOpen, onClose, portfolioId }) => {
    const queryClient = useQueryClient();
    const [newBalance, setNewBalance] = useState<number | null>(null);

    // Mutation for resetting portfolio
    const resetPortfolioMutation = useTrpcMutation(
        trpc.user.resetPortfolio.mutationOptions({
            onSuccess: () => {
                void queryClient.invalidateQueries({
                    queryKey: trpc.user.fetchPortfolio.queryOptions({ id: parseInt(portfolioId || '0'), shouldFetchTrades: true }).queryKey
                });
            }
        }),
        {
            successMessage: 'Portfolio reset successfully',
            fallbackErrorMessage: 'Failed to reset portfolio'
        }
    );

    const handleResetPortfolio = useCallback(() => {
        if (!portfolioId || newBalance === null) return;

        void resetPortfolioMutation.mutateAsync({
            id: parseInt(portfolioId),
            balance: newBalance
        });

        onClose();
        setNewBalance(null);
    }, [portfolioId, newBalance, resetPortfolioMutation, onClose]);

    return (
        <Modal isOpen={isOpen} title="Confirm" onClose={onClose}>
            <div className="space-y-4">
                <p className="text-zinc-200">
                    Are you sure you want to reset this portfolio? All trades will be lost, settings will be preserved.
                </p>
                <FormNumberInput
                    id="portfolioBalance"
                    label="New Balance"
                    min={0}
                    placeholder="Enter new balance"
                    value={newBalance}
                    onValueChange={setNewBalance}
                />
                <div className="flex justify-end mt-4 space-x-2">
                    <Button className="mr-2" variant="secondary" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleResetPortfolio}>
                        Reset
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default ResetPortfolioModal;
